import type { PipingSystem } from '../types';
import type { ExportColumn } from '../hooks/useExportColumns';

const COMPONENT_COLUMN_LABEL_PREFIX = 'Component: ';
export const COMPONENT_PROPS_KEY = '__componentProps';

export type ComponentParameterValues = Record<string, string[]>;

const ComponentValuePreviewLimit = 3;

function normalizeParameterName(value: string | undefined): string {
  return String(value ?? '').trim();
}

function getCaseInsensitiveValue(
  source: ComponentParameterValues | undefined,
  parameterName: string
): string[] {
  if (!source) {
    return [];
  }

  const direct = source[parameterName];
  if (Array.isArray(direct)) {
    return direct;
  }

  const matchedKey = Object.keys(source).find(
    (key) => key.toLowerCase() === parameterName.toLowerCase()
  );
  const matched = matchedKey ? source[matchedKey] : undefined;
  return Array.isArray(matched) ? matched : [];
}

export function createComponentColumnLabel(parameterName: string): string {
  return `${COMPONENT_COLUMN_LABEL_PREFIX}${normalizeParameterName(parameterName)}`;
}

export function getComponentColumnParameterName(column: Pick<ExportColumn, 'label' | 'revitParameter'>): string | null {
  const candidates = [column.revitParameter, column.label];
  for (const candidate of candidates) {
    const normalized = normalizeParameterName(candidate);
    if (normalized.toLowerCase().startsWith(COMPONENT_COLUMN_LABEL_PREFIX.toLowerCase())) {
      const parameterName = normalized.slice(COMPONENT_COLUMN_LABEL_PREFIX.length).trim();
      return parameterName.length > 0 ? parameterName : null;
    }
  }

  return null;
}

function getPipingSystemComponentValues(system: PipingSystem, parameterName: string): string[] {
  const componentProps = system.componentProps ?? system[COMPONENT_PROPS_KEY];
  return getCaseInsensitiveValue(componentProps, parameterName)
    .map((value) => String(value ?? '').trim())
    .filter((value) => value.length > 0);
}

export function formatPipingSystemComponentColumnValue(system: PipingSystem, parameterName: string): string {
  const values = getPipingSystemComponentValues(system, parameterName);
  if (values.length <= ComponentValuePreviewLimit) {
    return values.join(', ');
  }

  const preview = values.slice(0, ComponentValuePreviewLimit).join(', ');
  return `${preview} +${values.length - ComponentValuePreviewLimit} more`;
}

